import React, { useMemo, useRef } from 'react'
import { observer } from 'mobx-react'

import { ReactSelect as Select } from 'components'
import BlockClickTooltipped from 'components/BlockClickTooltipped'
import { ALL_LANGUAGES } from 'consts'
import { ListTerrotoriesSortFields, ListTerrotoriesSortOrder } from 'generated/graphql'
import { useTerritoryLazyQueries } from 'hooks-queries/territory'
import { client } from 'lib'
import { useTranslate } from 'lib/tolgee'
import { useIsGlobalTaxonomy } from 'modules/global-topic/common/global-topic/hooks/use-is-global-taxonomy'
import { getTopicsCurrentKey } from 'pages/Compare/utils/getTopicsCurrentKey'
import { UserStore } from 'stores'
import { TOLGEE_GLOBALS, captalizeFirstLetter } from 'utils'

import CustomGroupHeadingLabel from './components/CustomGroupHeadingLabel'
import SelectedItems from './components/SelectedItems'
import { TOPICS_QUERY, SUBTOPICS_QUERY, SAVED_POST_SEARCHES_QUERY } from './query'

import './index.scss'

const TOLGEE_PREFIX = 'ComparisonSelector'

function TerritorySelector({ filters, setFilters, loading = false, subtitle }) {
  const t = useTranslate()
  const typingTimer = useRef(null)

  const { isGlobalTaxonomyWorkspace, isQueryInformationAvailable } = useIsGlobalTaxonomy()

  const { comparingValues, count } = useMemo(() => {
    return {
      comparingValues: {
        topics: filters?.topics ?? [],
        subtopics: filters?.subtopics ?? [],
        savedPostSearches: filters?.savedPostSearches ?? [],
        global_topic: filters?.global_topic ?? [],
      },
      count: {
        topics:
          (filters?.savedPostSearches?.length ?? 0) +
          (filters?.topics?.length ?? 0) +
          (filters?.subtopics?.length ?? 0) +
          (filters?.global_topic?.length ?? 0),
      },
    }
  }, [filters?.topics, filters?.subtopics, filters?.savedPostSearches, filters?.global_topic])

  const userLanguages = UserStore.userLanguageOptions
    .filter(language => !language.disabled)
    .map(language => language.value)
  const availableLanguages = [...userLanguages, 'all']

  const limits = {
    topics: 5,
  }

  function onType(value, callback) {
    clearTimeout(typingTimer?.current)

    typingTimer.current = setTimeout(() => {
      fetchTopicsSuggest(value).then(callback)
    }, 500)
  }

  function removeItem(item) {
    const key = getTopicsCurrentKey(item)
    const newValues = comparingValues?.[key]?.filter(e => e?.value !== item?.value)

    setFilters(prevState => ({
      ...prevState,
      [key]: newValues,
    }))
  }

  function addToComparison(filterValue) {
    if (!filterValue?.value || count?.topics >= limits?.topic) {
      return
    }

    const key = getTopicsCurrentKey(filterValue)
    const isDuplicate = comparingValues?.[key]?.some(item => item?.value === filterValue?.value)

    if (isDuplicate) {
      return
    }

    if (key === 'savedPostSearches') {
      const savedPostSearch = filterValue?.savedPostSearch ?? {}
      // to avoid differences on refresh, this should reflect saved post search filter
      const newValue = {
        value: savedPostSearch?.id,
        label: savedPostSearch?.name,
        query: savedPostSearch?.query,
        type: savedPostSearch?.__typename,
      }
      const newValues = [...(comparingValues?.[key] || []), newValue]

      return setFilters(prevState => ({
        ...prevState,
        [key]: newValues,
      }))
    }

    if (isGlobalTaxonomyWorkspace && isQueryInformationAvailable) {
      const newValue = {
        value: filterValue?.value,
        label: filterValue?.label,
        type: filterValue?.type,
      }
      const newValues = [...(comparingValues?.[key] || []), newValue]

      return setFilters(prevState => ({
        ...prevState,
        [key]: newValues,
      }))
    }

    const topic = filterValue?.topic ?? {}
    const topicType = topic?.__typename?.toLowerCase()
    // to avoid differences on refresh, this should reflect topics/subtopics filter
    const newValue = {
      label: topic?.title,
      language: topic?.language,
      type: topicType === 'topic' && topic?.language === 'all' ? 'globalTopic' : topicType,
      value: Number(topic?.id),
      ...(topic?.category?.title && { category: topic.category.title }),
      ...(topic?.category?.topicGroupId && { groupId: topic.category.topicGroupId }),
      ...(topic?.topic?.title && { topic: topic.topic.title }),
    }
    const newValues = [...(comparingValues?.[key] || []), newValue]

    return setFilters(prevState => ({
      ...prevState,
      [key]: newValues,
    }))
  }

  function topicsToOpts(topics, type) {
    return topics?.map(topic => {
      const initialLanguage = ALL_LANGUAGES?.find(language => language?.value === topic?.language)
      const language = topic?.language === 'all' ? 'Music' : initialLanguage?.label

      return {
        label: topic?.title,
        subLabel: type === 'subtopic' ? `${language} - ${captalizeFirstLetter(topic?.topic?.title)}` : language,
        type,
        value: Number(topic?.id),
        // pass original topic/subtopic to populate filter
        topic,
      }
    })
  }

  function savedPostSearchesToOpts(savedPostSearches) {
    const a = savedPostSearches?.map(savedPostSearch => {
      const searchTerm =
        savedPostSearch?.query?.length > 40 ? `${savedPostSearch?.query?.slice(0, 40)}...` : savedPostSearch?.query

      return {
        label: savedPostSearch?.name,
        ...(searchTerm && {
          subLabel: t({
            key: `${TOLGEE_PREFIX}-searchTerm`,
            parameters: { searchTerm },
            defaultValue: 'Search term: {searchTerm}',
          }),
        }),
        value: savedPostSearch?.id,
        type: savedPostSearch?.__typename,
        // pass original saved search to populate filter
        savedPostSearch,
      }
    })

    return a
  }

  const [getTerritories] = useTerritoryLazyQueries({
    errorLog: {
      from: 'components/DashboardModals/CreateDashboardModal/components/TerritorySelector',
      message: 'Failed to fetch territories',
      name: 'getBasicTerritoriesList',
    },
    queryName: 'getBasicTerritoriesList',
  })

  function fetchTopicsSuggest(title) {
    if (isQueryInformationAvailable && isGlobalTaxonomyWorkspace) {
      return new Promise((resolve, reject) => {
        Promise.all([fetchSavedPostSearches(title), fetchGlobalTaxonomyTopics(title)])
          .then(data => {
            const topicsOptions = [
              {
                label: t(TOLGEE_GLOBALS.ownedTopics),
                options: data?.[0],
              },
              {
                label: t(TOLGEE_GLOBALS.topics),
                options: data?.[1],
              },
            ]

            resolve(topicsOptions)
          })
          .catch(res => {
            reject(res)
          })
      })
    }
    return new Promise((resolve, reject) => {
      Promise.all([fetchSavedPostSearches(title), fetchSubtopics(title), fetchTopics(title), fetchGlobalTopics(title)])
        .then(data => {
          const topicsOptions = [
            {
              label: t(TOLGEE_GLOBALS.ownedTopics),
              options: data?.[0],
            },
            {
              label: t(TOLGEE_GLOBALS.subtopics),
              options: data?.[1],
            },
            {
              label: t(TOLGEE_GLOBALS.topics),
              options: data?.[2],
            },
            {
              label: t(TOLGEE_GLOBALS.musicTopics),
              options: data?.[3],
              type: 'globalTopic',
            },
          ]

          resolve(topicsOptions)
        })
        .catch(res => {
          reject(res)
        })
    })
  }

  function fetchTopics(title) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query: TOPICS_QUERY,
          variables: {
            term: title,
            languages: availableLanguages,
          },
        })
        .then(({ data }) => {
          const topics = data?.getSuggest?.topics

          resolve(topicsToOpts(topics, 'topic'))
        })
        .catch(res => {
          reject(res)
        })
    })
  }

  function fetchGlobalTopics(title) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query: TOPICS_QUERY,
          variables: {
            term: title,
            languages: ['all'],
          },
        })
        .then(({ data }) => {
          const globalTopics = data?.getSuggest?.topics

          resolve(topicsToOpts(globalTopics, 'globalTopic'))
        })
        .catch(res => {
          reject(res)
        })
    })
  }

  function fetchSubtopics(title) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query: SUBTOPICS_QUERY,
          variables: {
            term: title,
            languages: availableLanguages,
          },
        })
        .then(({ data }) => {
          const subtopics = data?.getSuggest?.subtopics

          resolve(topicsToOpts(subtopics, 'subtopic'))
        })
        .catch(res => {
          reject(res)
        })
    })
  }

  function fetchSavedPostSearches(title) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query: SAVED_POST_SEARCHES_QUERY,
          variables: {
            term: title,
          },
        })
        .then(({ data }) => {
          const savedPostSearches = data?.getSuggest?.savedPostSearches

          resolve(savedPostSearchesToOpts(savedPostSearches))
        })
        .catch(res => {
          reject(res)
        })
    })
  }

  function fetchGlobalTaxonomyTopics(title) {
    return new Promise((resolve, reject) => {
      getTerritories({
        variables: {
          pagination: {
            take: 25,
          },
          sort: {
            field: ListTerrotoriesSortFields.AvgEngagement,
            order: ListTerrotoriesSortOrder.Desc,
          },
          filters: {
            title,
          },
        },
      })
        .then(({ data }) => {
          const globalTopics = data?.listTerritoriesPaginated?.data || []

          const formattedGlobalTopics = globalTopics.map(territory => {
            return {
              label: territory?.name,
              value: territory?.territoryId,
              type: 'global_topic',
            }
          })

          resolve(formattedGlobalTopics)
        })
        .catch(res => {
          reject(res)
        })
    })
  }

  const isFull = count?.topics >= limits?.topics

  const placeholder = t({
    key: `${TOLGEE_PREFIX}-topicInputPlaceholderWithOwnedTopic`,
    defaultValue: 'Search for a topic, a subtopic or an owned topic',
  })

  return (
    <div className="TerritorySelector">
      <div className="compare-topic-input-group">
        <div className="compare-input-component">
          <BlockClickTooltipped
            tooltipPositionClassname="tooltipped-s"
            conditions={[
              {
                isBlocked: isFull,
                message: `You can only compare ${limits.topics} topics and subtopics at the same time.\nRemove a topics or subtopic to add a new one.`,
              },
            ]}
          >
            {isBlocked => (
              <Select
                key="topics"
                asyncSelect
                containerClass="TerritorySelector-input-container"
                className="filter"
                onChange={addToComparison}
                placeholder={placeholder}
                value=""
                loadOptions={onType}
                noOptionsMessage={({ inputValue }) => {
                  return !inputValue ? null : t(TOLGEE_GLOBALS.noResultsFound)
                }}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                  GroupHeading: CustomGroupHeadingLabel,
                }}
                isDisabled={isBlocked || loading}
              />
            )}
          </BlockClickTooltipped>
          {subtitle && <p className="TerritorySelector-subtitle">{subtitle}</p>}
        </div>

        <div className="selected-elements-container">
          <SelectedItems type="topics" items={comparingValues} removeItem={removeItem} />
        </div>
      </div>
    </div>
  )
}

export default observer(TerritorySelector)
